import {
  queryOptions as queryOptionsBuilder,
  useMutation,
  useQuery,
  useSuspenseQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult
} from '@tanstack/react-query';

import type {
  FindNotificationResponse,
  ListNotificationPreferenceItemResponse,
  NotificationFindParams,
  ReadNotificationsRequest,
  UpdateNotificationDto,
  UpdateNotificationPreferenceRequest
} from '../model';

import { customInstance } from '../../mutator/custom-instance-new.ts';
import type { ErrorType , BodyType } from '../../mutator/custom-instance-new.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Find notifications
 */
export const notificationFind = (
    params: NotificationFindParams,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<FindNotificationResponse>(
      {url: `/notifications`, method: 'GET',
        params, signal
    },
      options);
    }




export const getNotificationFindQueryKey = (params?: NotificationFindParams,) => {
    return [
    `/notifications`, ...(params ? [params] : [])
    ] as const;
    }


export const getNotificationFindQueryOptions = <TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(params: NotificationFindParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNotificationFindQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof notificationFind>>> = ({ signal }) => notificationFind(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NotificationFindQueryResult = NonNullable<Awaited<ReturnType<typeof notificationFind>>>
export type NotificationFindQueryError = ErrorType<unknown>


export function useNotificationFind<TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(
 params: NotificationFindParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof notificationFind>>,
          TError,
          Awaited<ReturnType<typeof notificationFind>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNotificationFind<TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(
 params: NotificationFindParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof notificationFind>>,
          TError,
          Awaited<ReturnType<typeof notificationFind>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNotificationFind<TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(
 params: NotificationFindParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Find notifications
 */

export function useNotificationFind<TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(
 params: NotificationFindParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNotificationFindQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getNotificationFindSuspenseQueryOptions = <TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(params: NotificationFindParams, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNotificationFindQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof notificationFind>>> = ({ signal }) => notificationFind(params, requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type NotificationFindSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof notificationFind>>>
export type NotificationFindSuspenseQueryError = ErrorType<unknown>


export function useNotificationFindSuspense<TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(
 params: NotificationFindParams, options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNotificationFindSuspense<TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(
 params: NotificationFindParams, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNotificationFindSuspense<TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(
 params: NotificationFindParams, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Find notifications
 */

export function useNotificationFindSuspense<TData = Awaited<ReturnType<typeof notificationFind>>, TError = ErrorType<unknown>>(
 params: NotificationFindParams, options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationFind>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNotificationFindSuspenseQueryOptions(params,options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * @summary Mark notifications as read
 */
export const notificationRead = (
    readNotificationsRequest: BodyType<ReadNotificationsRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/notifications/read`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: readNotificationsRequest, signal
    },
      options);
    }




export const getNotificationReadMutationKey = () => ['notificationRead'] as const;

export const getNotificationReadMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof notificationRead>>, TError,NotificationReadMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof notificationRead>>, TError,NotificationReadMutationVariables, TContext> => {

const mutationKey = getNotificationReadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof notificationRead>>, NotificationReadMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  notificationRead(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type NotificationReadMutationResult = NonNullable<Awaited<ReturnType<typeof notificationRead>>>
    export type NotificationReadMutationBody = BodyType<ReadNotificationsRequest>
    export type NotificationReadMutationError = ErrorType<void>
    export type NotificationReadMutationVariables = {data: BodyType<ReadNotificationsRequest>}

    /**
 * @summary Mark notifications as read
 */
export const useNotificationRead = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof notificationRead>>, TError,NotificationReadMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof notificationRead>>,
        TError,
        NotificationReadMutationVariables,
        TContext
      > => {
      return useMutation(getNotificationReadMutationOptions(options), queryClient);
    }
    /**
 * @summary Update a notification read status
 */
export const notificationUpdate = (
    id: number,
    updateNotificationDto: BodyType<UpdateNotificationDto>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/notification/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateNotificationDto, signal
    },
      options);
    }




export const getNotificationUpdateMutationKey = () => ['notificationUpdate'] as const;

export const getNotificationUpdateMutationOptions = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof notificationUpdate>>, TError,NotificationUpdateMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof notificationUpdate>>, TError,NotificationUpdateMutationVariables, TContext> => {

const mutationKey = getNotificationUpdateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof notificationUpdate>>, NotificationUpdateMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  notificationUpdate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type NotificationUpdateMutationResult = NonNullable<Awaited<ReturnType<typeof notificationUpdate>>>
    export type NotificationUpdateMutationBody = BodyType<UpdateNotificationDto>
    export type NotificationUpdateMutationError = ErrorType<void>
    export type NotificationUpdateMutationVariables = {id: number;data: BodyType<UpdateNotificationDto>}

    /**
 * @summary Update a notification read status
 */
export const useNotificationUpdate = <TError = ErrorType<void>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof notificationUpdate>>, TError,NotificationUpdateMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof notificationUpdate>>,
        TError,
        NotificationUpdateMutationVariables,
        TContext
      > => {
      return useMutation(getNotificationUpdateMutationOptions(options), queryClient);
    }
    /**
 * @summary Update notification preferences for event types
 */
export const notificationUpdatePreferences = (
    updateNotificationPreferenceRequest: BodyType<UpdateNotificationPreferenceRequest>,
 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<void>(
      {url: `/notification-preference`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateNotificationPreferenceRequest, signal
    },
      options);
    }




export const getNotificationUpdatePreferencesMutationKey = () => ['notificationUpdatePreferences'] as const;

export const getNotificationUpdatePreferencesMutationOptions = <TError = ErrorType<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof notificationUpdatePreferences>>, TError,NotificationUpdatePreferencesMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof notificationUpdatePreferences>>, TError,NotificationUpdatePreferencesMutationVariables, TContext> => {

const mutationKey = getNotificationUpdatePreferencesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof notificationUpdatePreferences>>, NotificationUpdatePreferencesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  notificationUpdatePreferences(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type NotificationUpdatePreferencesMutationResult = NonNullable<Awaited<ReturnType<typeof notificationUpdatePreferences>>>
    export type NotificationUpdatePreferencesMutationBody = BodyType<UpdateNotificationPreferenceRequest>
    export type NotificationUpdatePreferencesMutationError = ErrorType<unknown>
    export type NotificationUpdatePreferencesMutationVariables = {data: BodyType<UpdateNotificationPreferenceRequest>}

    /**
 * @summary Update notification preferences for event types
 */
export const useNotificationUpdatePreferences = <TError = ErrorType<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof notificationUpdatePreferences>>, TError,NotificationUpdatePreferencesMutationVariables, TContext>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof notificationUpdatePreferences>>,
        TError,
        NotificationUpdatePreferencesMutationVariables,
        TContext
      > => {
      return useMutation(getNotificationUpdatePreferencesMutationOptions(options), queryClient);
    }
    /**
 * @summary List notification preferences for the current user
 */
export const notificationListPreferences = (

 options?: SecondParameter<typeof customInstance>,signal?: AbortSignal
) => {


      return customInstance<ListNotificationPreferenceItemResponse[]>(
      {url: `/notification-preferences`, method: 'GET', signal
    },
      options);
    }




export const getNotificationListPreferencesQueryKey = () => {
    return [
    `/notification-preferences`
    ] as const;
    }


export const getNotificationListPreferencesQueryOptions = <TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNotificationListPreferencesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof notificationListPreferences>>> = ({ signal }) => notificationListPreferences(requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type NotificationListPreferencesQueryResult = NonNullable<Awaited<ReturnType<typeof notificationListPreferences>>>
export type NotificationListPreferencesQueryError = ErrorType<unknown>


export function useNotificationListPreferences<TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof notificationListPreferences>>,
          TError,
          Awaited<ReturnType<typeof notificationListPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNotificationListPreferences<TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof notificationListPreferences>>,
          TError,
          Awaited<ReturnType<typeof notificationListPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNotificationListPreferences<TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List notification preferences for the current user
 */

export function useNotificationListPreferences<TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNotificationListPreferencesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getNotificationListPreferencesSuspenseQueryOptions = <TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>( options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getNotificationListPreferencesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof notificationListPreferences>>> = ({ signal }) => notificationListPreferences(requestOptions, signal);





   return  queryOptionsBuilder({ queryKey, queryFn, ...queryOptions}) as UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> } & { throwOnError?: ((this: never, error: TError) => boolean) & { readonly __inferenceOnly: never } }
}

export type NotificationListPreferencesSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof notificationListPreferences>>>
export type NotificationListPreferencesSuspenseQueryError = ErrorType<unknown>


export function useNotificationListPreferencesSuspense<TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>(
  options: { query:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNotificationListPreferencesSuspense<TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useNotificationListPreferencesSuspense<TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
  ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List notification preferences for the current user
 */

export function useNotificationListPreferencesSuspense<TData = Awaited<ReturnType<typeof notificationListPreferences>>, TError = ErrorType<unknown>>(
  options?: { query?:Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof notificationListPreferences>>, TError, TData>>, request?: SecondParameter<typeof customInstance>}
 , queryClient?: QueryClient
 ):  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getNotificationListPreferencesSuspenseQueryOptions(options)

  const query = useSuspenseQuery(queryOptions, queryClient) as  UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






